import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { hashPassword } from '@adornis/users/server/password.js';
import { createNewUser } from '@adornis/users/server/util.js';
import { validate } from '@adornis/validation/decorators.js';
import { BaseError } from '@adornis/validation/errors/BaseError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Registermethode, StudentType } from '@chancenwerk/enums/enums.js';
import { from } from 'rxjs';
import { validateActivationCode } from '../../../HefteTeacherActivationCode.js';
import { Role } from '../../../semantics/Role.js';
import { HEFTE_WORDLIST } from '../constants/hefte-wordlist.js';
import { ChancenwerkUser } from './ChancenwerkUser.js';
import { HefteStudent } from './HefteStudent.js';
import { EntryInformation, SchoolInformation } from './Student.js';

const MAX_STUDENTS_PER_TEACHER = 40;

@Entity()
export class HefteTeacher extends ChancenwerkUser {
  public static override _class = 'HefteTeacher';
  public static override readableName = 'Hefte-Lehrkraft';

  constructor(doc: PartialEntityData<HefteTeacher>) {
    super(doc);
  }

  @validate(nonOptional({ translationKey: 'validation.firstName' }))
  @Field(() => String)
  public override vorname?: string;

  @validate(nonOptional({ translationKey: 'validation.lastName' }))
  @Field(() => String)
  public override nachname?: string;

  @Field(() => [ID], { default: v => v ?? [] })
  public hefteStudentIDs: Maybe<string[]>;

  @Field(() => Int, { default: v => v ?? MAX_STUDENTS_PER_TEACHER })
  public maxStudents: Maybe<number>;

  @Field(() => Int, { default: v => v ?? 13 })
  public classGradeLevel: Maybe<number>;
}

// --- API ---

export const registerHefteTeacher = registerMutation({
  type: () => String,
  operationName: 'registerHefteTeacher',
  params: [
    { type: () => String, name: 'activationCode' },
    { type: () => String, name: 'vorname' },
    { type: () => String, name: 'nachname' },
    { type: () => String, name: 'email' },
    { type: () => String, name: 'password' },
  ],
  resolve: (activationCode: string, vorname: string, nachname: string, email: string, password: string) => {
    return async () => {
      const isValid = await validateActivationCode(activationCode);
      if (!isValid) {
        throw new BaseError('Ungültiger Freischalt-Code.', { translationKey: 'hefteTeacher.invalidActivationCode' });
      }

      const normalizedEmail = email.trim();
      const username = normalizedEmail;
      const collection = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
      const existingUser = await collection.findOne({
        username: { $regex: `^${username.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' },
      });
      if (existingUser) {
        throw new BaseError('Benutzername ist bereits vergeben.', { translationKey: 'users.username_in_use' });
      }

      const teacher = new HefteTeacher({
        vorname,
        nachname,
        email: normalizedEmail,
        username,
        _class: HefteTeacher._class,
        roles: [new AdornisRoleToContextsWrapper({ name: Role.HEFTE_TEACHER, contexts: [GLOBAL_CONTEXT] })],
        hefteStudentIDs: [],
        maxStudents: MAX_STUDENTS_PER_TEACHER,
        classGradeLevel: 13,
      });

      const id = await AdornisUser.createEntity(HefteTeacher._class, teacher)();
      await collection.updateOne({ _id: id }, { $set: { password: hashPassword(password) } });

      // Log in the teacher
      if (!context.serverContext) {
        context.userID = id;
        context.userClass = HefteTeacher._class;
        context.impersonatingUser = undefined;
        context.impersonatingUserClass = undefined;
        await context.recreate();
      }

      return id;
    };
  },
});

export const subscribeHefteTeacherStudents = registerSubscription({
  type: () => [HefteStudent],
  operationName: 'subscribeHefteTeacherStudents',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
  ],
  resolve: (skip: number, limit: number) => {
    return (gqlFields: BaseQLSelectionSet<HefteStudent>) =>
      from(
        (async () => {
          const teacher = await getAuthenticatedTeacher();
          const studentIDs = teacher.hefteStudentIDs ?? [];
          if (!studentIDs.length) {
            return [];
          }

          const studentCol = await getRawCollection<HefteStudent>(HefteStudent._collectionName);
          const students = await studentCol.find({ _id: { $in: studentIDs } }, { projection: gqlFields }).toArray();
          const studentByID = new Map(students.map(student => [student._id, student]));
          return studentIDs
            .map(studentID => studentByID.get(studentID))
            .filter((student): student is HefteStudent => Boolean(student));
        })(),
      );
  },
});

export const updateHefteTeacherClassGradeLevel = registerMutation({
  type: () => Boolean,
  operationName: 'updateHefteTeacherClassGradeLevel',
  params: [{ type: () => Int, name: 'classGradeLevel' }],
  resolve: (classGradeLevel: number) => {
    return async () => {
      if (classGradeLevel < 1 || classGradeLevel > 13) {
        throw new BaseError('Klassenstufe muss zwischen 1 und 13 liegen.');
      }

      const teacher = await getAuthenticatedTeacher();

      const teacherCol = await getRawCollection<HefteTeacher>(AdornisUser._collectionName);
      await teacherCol.updateOne({ _id: teacher._id }, { $set: { classGradeLevel } });

      if (teacher.hefteStudentIDs?.length) {
        const studentCol = await getRawCollection<HefteStudent>(HefteStudent._collectionName);
        await studentCol.updateMany(
          { _id: { $in: teacher.hefteStudentIDs } },
          { $set: { 'schoolInformation.jahrgangsstufe': classGradeLevel } },
        );
      }

      return true;
    };
  },
});

export const updateHefteTeacherProfile = registerMutation({
  type: () => Boolean,
  operationName: 'updateHefteTeacherProfile',
  params: [
    { type: () => String, name: 'vorname' },
    { type: () => String, name: 'nachname' },
    { type: () => String, name: 'email' },
  ],
  resolve: (vorname: string, nachname: string, email: string) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself<HefteTeacher>()({ _id: 1, _class: 1 });
      if (!user || user._class !== HefteTeacher._class) {
        throw new BaseError('Nicht autorisiert.');
      }

      const col = await getRawCollection<HefteTeacher>(AdornisUser._collectionName);
      await col.updateOne({ _id: user._id }, { $set: { vorname, nachname, email } });
      return true;
    };
  },
});

export const getHefteTeacherStudents = registerQuery({
  type: () => [HefteTeacherStudentInfo],
  operationName: 'getHefteTeacherStudents',
  params: [],
  resolve: () => {
    return async (gqlFields: BaseQLSelectionSet<HefteTeacherStudentInfo>) => {
      const user = await CurrentUserInfo.getMyself<HefteTeacher>()({
        _id: 1,
        _class: 1,
        hefteStudentIDs: 1,
      });
      if (!user || user._class !== HefteTeacher._class) {
        throw new BaseError('Nicht autorisiert.');
      }

      if (!user.hefteStudentIDs?.length) return [];

      const col = await getRawCollection<HefteStudent>(HefteStudent._collectionName);
      const students = await col
        .find({ _id: { $in: user.hefteStudentIDs } }, { projection: { _id: 1, username: 1, plainTextPassword: 1 } })
        .toArray();

      return students.map(
        s =>
          new HefteTeacherStudentInfo({
            _id: s._id,
            username: s.username,
            plainTextPassword: s.plainTextPassword,
          }),
      );
    };
  },
});

@Entity()
export class HefteTeacherStudentInfo extends AdornisEntity {
  public static override _class = 'HefteTeacherStudentInfo';

  @Field(() => ID)
  _id: Maybe<string>;

  @Field(() => String)
  username: Maybe<string>;

  @Field(() => String)
  plainTextPassword: Maybe<string>;
}

const SPECIAL_CHARACTERS = ['!', '$', '@', '#', '%', '&', '?', '+', '*', '~', '§'];
const generatePrefix = (): string => {
  return SPECIAL_CHARACTERS[Math.floor(Math.random() * SPECIAL_CHARACTERS.length)]!;
};
function getRandomHefteWord(): string {
  return HEFTE_WORDLIST[Math.floor(Math.random() * HEFTE_WORDLIST.length)]!;
}

function generateWordSequence(words: number = 2): string {
  return new Array(words)
    .fill(null)
    .map(() => getRandomHefteWord())
    .join('');
}

function generateHeftePassword(): string {
  const prefix = generatePrefix();
  const words = generateWordSequence(2);
  const digits = Math.floor(100 + Math.random() * 900);
  return `${prefix}${words}${digits}`;
}

async function ensureUniqueHefteUsername(): Promise<string> {
  const col = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
  let phrase: string;
  let attempts = 0;
  do {
    phrase = generateWordSequence();
    const existing = await col.findOne({ username: phrase });
    if (!existing) return phrase;
    attempts++;
  } while (attempts < 100);
  throw new BaseError('Konnte keinen einzigartigen Zugangscode generieren.');
}

async function getAuthenticatedTeacher(): Promise<EntityData<HefteTeacher>> {
  const myself = await CurrentUserInfo.getMyself<HefteTeacher>()({ _id: 1, _class: 1 });
  if (!myself || myself._class !== HefteTeacher._class) {
    throw new BaseError('Nicht autorisiert.');
  }
  const col = await getRawCollection<EntityData<HefteTeacher>>(AdornisUser._collectionName);
  const teacher = await col.findOne(
    { _id: myself._id },
    { projection: { _id: 1, _class: 1, hefteStudentIDs: 1, maxStudents: 1, classGradeLevel: 1 } },
  );
  if (!teacher) throw new BaseError('Nicht autorisiert.');
  return teacher;
}

const createHefteStudent = registerMutation({
  type: () => HefteTeacherStudentInfo,
  operationName: 'createHefteStudent',
  params: [],
  resolve: () => {
    return async (gqlFields: BaseQLSelectionSet<HefteTeacherStudentInfo>) => {
      const teacher = await getAuthenticatedTeacher();
      const teacherClassGradeLevel = teacher.classGradeLevel ?? 13;
      if (!teacher.classGradeLevel) {
        const teacherCol = await getRawCollection<HefteTeacher>(AdornisUser._collectionName);
        await teacherCol.updateOne({ _id: teacher._id }, { $set: { classGradeLevel: teacherClassGradeLevel } });
      }
      const currentCount = teacher.hefteStudentIDs?.length ?? 0;
      if (currentCount >= (teacher.maxStudents ?? MAX_STUDENTS_PER_TEACHER)) {
        throw new BaseError(
          `Maximale Anzahl von ${teacher.maxStudents ?? MAX_STUDENTS_PER_TEACHER} Schülern erreicht.`,
        );
      }

      const username = await ensureUniqueHefteUsername();
      const plainTextPassword = generateHeftePassword();

      const student = new HefteStudent({
        username,
        vorname: username,
        nachname: '',
        email: `dummy-hefte-${username}@chancenwerk.de`,
        _class: HefteStudent._class,
        schuelertyp: StudentType.US,
        schoolInformation: new SchoolInformation({
          jahrgangsstufe: teacherClassGradeLevel,
        }),
        joinInformation: new EntryInformation({
          registermethode: Registermethode.HEFTE,
          campusRegisterDataSend: true,
        }),
        roles: [new AdornisRoleToContextsWrapper({ name: Role.HEFTE_STUDENT, contexts: [GLOBAL_CONTEXT] })],
        createdBy: teacher._id,
      });
      student.plainTextPassword = plainTextPassword;

      const id = await createNewUser(student, plainTextPassword);

      // Add student to teacher's list
      const teacherCol = await getRawCollection<EntityData<HefteTeacher>>(AdornisUser._collectionName);
      // @ts-expect-error
      await teacherCol.updateOne({ _id: teacher._id }, { $push: { hefteStudentIDs: id } });

      return new HefteTeacherStudentInfo({
        _id: id,
        username,
        plainTextPassword,
      });
    };
  },
});

const createHefteStudentsBulk = registerMutation({
  type: () => [HefteTeacherStudentInfo],
  operationName: 'createHefteStudentsBulk',
  params: [{ type: () => Int, name: 'count' }],
  resolve: (count: number) => {
    return async (gqlFields: BaseQLSelectionSet<HefteTeacherStudentInfo>) => {
      const teacher = await getAuthenticatedTeacher();
      const currentCount = teacher.hefteStudentIDs?.length ?? 0;
      const max = teacher.maxStudents ?? MAX_STUDENTS_PER_TEACHER;

      if (count < 1 || count > max) {
        throw new BaseError(`Anzahl muss zwischen 1 und ${max} liegen.`);
      }
      if (currentCount + count > max) {
        throw new BaseError(
          `Maximale Anzahl von ${max} Schülern würde überschritten. Noch ${max - currentCount} verfügbar.`,
        );
      }

      const results: HefteTeacherStudentInfo[] = [];
      for (let i = 0; i < count; i++) {
        const result = await createHefteStudent()({ _id: 1, username: 1, plainTextPassword: 1 });
        results.push(result);
      }
      return results;
    };
  },
});

const deleteHefteStudent = registerMutation({
  type: () => Boolean,
  operationName: 'deleteHefteStudent',
  params: [{ type: () => String, name: 'studentID' }],
  resolve: (studentID: string) => {
    return async () => {
      const teacher = await getAuthenticatedTeacher();

      const userCol = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
      const deleteResult = await userCol.deleteOne({
        _id: studentID,
        _class: HefteStudent._class,
        createdBy: teacher._id,
      });
      if (deleteResult.deletedCount !== 1) {
        throw new BaseError('Schüler konnte nicht gelöscht werden. Bitte Seite neu laden und erneut versuchen.');
      }

      const teacherCol = await getRawCollection<HefteTeacher>(AdornisUser._collectionName);
      // @ts-expect-error
      await teacherCol.updateOne({ _id: teacher._id }, { $pull: { hefteStudentIDs: studentID } });

      return true;
    };
  },
});

export namespace HefteTeacherAPI {
  export const register = registerHefteTeacher;
  export const updateProfile = updateHefteTeacherProfile;
  export const updateClassGradeLevel = updateHefteTeacherClassGradeLevel;
  export const getStudents = getHefteTeacherStudents;
  export const subscribeStudents = subscribeHefteTeacherStudents;
  export const createStudent = createHefteStudent;
  export const createStudentsBulk = createHefteStudentsBulk;
  export const deleteStudent = deleteHefteStudent;
}
